'use client';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@shared/components/ui/dropdown-menu';
import { faPlus, faSliders, faTrash } from '@fortawesome/pro-light-svg-icons';
import { cn } from '@shared/utils/utils';
import { useTranslation } from 'react-i18next';
import { Button } from '../button';
import { Combobox, SmartCombobox } from './components';
import { useFilterConditions } from './hooks';
import type { AdvancedFilterProps, AdvancedFilterTranslations } from './types';

/**
 * Componente AdvancedFilterDesktop - Versión desktop del sistema de filtros avanzados
 *
 * Versión especializada para pantallas grandes con layout de grid optimizado.
 * Mantiene la funcionalidad completa del filtro avanzado con múltiples condiciones.
 *
 * Características:
 * - Layout de grid responsive para desktop
 * - Múltiples condiciones con operadores Y/O
 * - Auto-selección de primera acción disponible
 * - Debounce automático para evitar llamadas excesivas
 * - Contador visual de filtros activos
 * - Soporte para búsquedas dinámicas y estáticas
 * - Optimizado con React.memo y memoización
 * - Traducciones automáticas del namespace 'ui' + override opcional
 *
 * @param filterOptions - Configuración de columnas, acciones y valores disponibles
 * @param onChange - Callback ejecutado cuando cambian las condiciones válidas
 * @param initialConditions - Condiciones iniciales para hidratar el estado
 * @param translations - Textos traducidos opcionales para override específico
 */
const AdvancedFilterDesktop = ({ filterOptions, onChange, initialConditions, translations }: AdvancedFilterProps) => {
  // 🌍 Hook de traducción para namespace UI compartido
  const { t: tUI } = useTranslation('ui');

  // 🎯 Traducciones híbridas: por defecto desde 'ui' namespace + override opcional
  const t: AdvancedFilterTranslations = {
    filters: translations?.filters || tUI('advancedFilter.filters'),
    fulfillsWith: translations?.fulfillsWith || tUI('advancedFilter.fulfillsWith'),
    selectColumn: translations?.selectColumn || tUI('advancedFilter.selectColumn'),
    selectAction: translations?.selectAction || tUI('advancedFilter.selectAction'),
    selectValue: translations?.selectValue || tUI('advancedFilter.selectValue'),
    addCondition: translations?.addCondition || tUI('advancedFilter.addCondition'),
    notFound: translations?.notFound || tUI('advancedFilter.notFound'),
    and: translations?.and || tUI('advancedFilter.and'),
    or: translations?.or || tUI('advancedFilter.or'),
    enterText: translations?.enterText || tUI('advancedFilter.enterText', 'Escribir texto'),
    selectDate: translations?.selectDate || tUI('advancedFilter.selectDate', 'Seleccionar fecha'),
    selectDateRange: translations?.selectDateRange || tUI('advancedFilter.selectDateRange', 'Seleccionar rango'),
    from: translations?.from || tUI('advancedFilter.from', 'Desde'),
    to: translations?.to || tUI('advancedFilter.to', 'Hasta'),
  };

  const { conditions, activeFiltersCount, selectedColumns, addCondition, removeCondition, updateCondition } =
    useFilterConditions(filterOptions, onChange, initialConditions);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="relative">
          <Button
            variant="outline"
            type="button"
            size="sm"
            className={cn(
              'shadow-none transition-colors border border-input bg-background text-foreground/70 hover:bg-accent hover:text-accent-foreground',
            )}
            icon={faSliders}
            iconPosition="start"
            data-testid="filter-open-button"
          >
            <span className="hidden sm:block text-sm">{t.filters}</span>
          </Button>
          {/* 🎯 Badge de contador de filtros activos */}
          {activeFiltersCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-sm">
              {activeFiltersCount}
            </span>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        className="sm:min-w-[750px] sm:max-w-[750px] p-4 bg-body-bg text-foreground border border-card-border text-sm"
      >
        <div className="flex items-center justify-between mb-2 pt-1">
          <div className="text-sm font-semibold text-foreground">{t.filters}</div>
        </div>

        {conditions.map((condition, index) => {
          const selected = filterOptions.find((f) => f.column.value === condition.column);
          const columnOptions = filterOptions
            .map((f) => f.column)
            .filter((col) => col.value === condition.column || !selectedColumns.includes(col.value));

          const availableActions = selected?.actions || [];

          // 🛒 Detectar si es un filtro de productos para ajustar el grid
          const isProductFilter = selected?.values === 'product';
          const gridClasses = isProductFilter
            ? 'grid sm:grid-cols-[86px_22%_20%_35%_46px] gap-2 pt-2 border-b border-card-border sm:border-0 items-center w-full'
            : 'grid sm:grid-cols-[86px_27%_25%_25%_46px] gap-2 pt-2 border-b border-card-border sm:border-0 items-center w-full';

          return (
            <div key={condition.id} className={gridClasses}>
              {index === 0 ? (
                <div className="whitespace-nowrap text-sm font-semibold text-foreground">{t.fulfillsWith}</div>
              ) : (
                <Combobox
                  options={[
                    { label: t.and, value: 'y' },
                    { label: t.or, value: 'o' },
                  ]}
                  placeholder=""
                  value={condition.logic}
                  setValue={(val) => updateCondition(condition.id, 'logic', val)}
                  dataTestId={`filter-logic-${condition.id}`}
                />
              )}

              <Combobox
                options={columnOptions}
                placeholder={t.selectColumn}
                value={condition.column}
                setValue={(val) => updateCondition(condition.id, 'column', val)}
                dataTestId={`filter-column-${condition.id}`}
              />

              <Combobox
                options={availableActions}
                placeholder={t.selectAction}
                value={condition.action}
                setValue={(val) => updateCondition(condition.id, 'action', val)}
                disabled={!condition.column}
                dataTestId={`filter-action-${condition.id}`}
              />

              {/* 🆕 SmartCombobox que decide entre estático, dinámico, texto libre y fechas */}
              {selected ? (
                <SmartCombobox
                  filterOption={selected}
                  value={condition.value}
                  setValue={(val) => updateCondition(condition.id, 'value', val)}
                  placeholder={t.selectValue}
                  disabled={!condition.column || condition.action === 'is_null' || condition.action === 'is_not_null'}
                  dataTestId={`filter-value-${condition.id}`}
                  translations={{
                    notFound: t.notFound,
                    enterText: t.enterText,
                    selectDate: t.selectDate,
                    selectDateRange: t.selectDateRange,
                    from: t.from,
                    to: t.to,
                  }}
                />
              ) : (
                <div className="bg-background border border-border rounded px-2 py-[9px] text-sm text-muted-foreground opacity-70">
                  {t.selectValue}
                </div>
              )}

              <div>
                <Button
                  type="button"
                  size="lg"
                  variant="icon"
                  icon={faTrash}
                  className="bg-transparent border border-card-border ml-auto hover:bg-accent hover:text-accent-foreground transition-colors shadow-none"
                  onClick={() => removeCondition(condition.id)}
                  data-testid={`filter-remove-condition-${condition.id}`}
                />
              </div>
            </div>
          );
        })}

        <div className="flex items-center gap-3 pt-3">
          <div className="w-full flex justify-end items-center gap-3">
            <Button
              type="button"
              size="sm"
              variant="primary"
              bgcolor="transparent"
              className="w-auto hover:bg-accent hover:text-accent-foreground text-foreground transition-colors shadow-none"
              fullWidth={false}
              icon={faPlus}
              iconPosition="start"
              onClick={() => addCondition()}
              data-testid="filter-add-condition"
            >
              {t.addCondition}
            </Button>
          </div>
          <div className="w-[48px] hidden sm:block" />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AdvancedFilterDesktop;
